/**
 * Delivery Provider Selection Modal
 *
 * Allows users to select a delivery provider (FedEx, SF Express) for an outbound order
 * and create a shipment to get the waybill document and tracking number.
 *
 * Workflow:
 * 1. User selects delivery provider (FedEx, SF, etc.)
 * 2. System sends order to delivery provider API
 * 3. Returns waybill PDF and tracking number
 * 4. Saves to outbound order for display/printing
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Space,
  Divider,
  Alert,
  Spin,
  Card,
  Row,
  Col,
  Descriptions,
  message,
} from 'antd';
import {
  SendOutlined,
  PrinterOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { apiService } from '../../../services/apiService';
import { useTranslation } from "react-i18next";

const { Option } = Select;

interface DeliveryProviderModalProps {
  visible: boolean;
  onClose: () => void;
  outboundOrderId: string;
  outboundNumber: string;
  recipientName?: string;
  recipientAddress?: string;
  recipientCountryCode?: string;
  recipientPhone?: string;
  recipientPostalCode?: string;
  recipientCity?: string;
  recipientState?: string; // State/province code from the order (e.g. QLD, VIC, NY)
  totalAmount?: number;
  onSuccess?: (trackingNumber: string, labelPdf: string) => void;
}

interface ShipperInfo {
  address: {
    streetLines: string[];
    city: string;
    postalCode: string;
    countryCode: string;
    stateOrProvinceCode?: string;
  };
  contact: {
    personName?: string;
    companyName: string;
    phoneNumber: string;
    emailAddress?: string;
  };
}

// Default shipper info (can be loaded from company settings later)
const defaultShipperInfo: ShipperInfo = {
  address: {
    streetLines: ['5/F, Block B, Profit Industrial Building, 13 Kwai Hei Street, Kwai Chung'],
    city: 'Hong Kong',
    postalCode: '999077',
    countryCode: 'HK',
  },
  contact: {
    companyName: 'Shoptoplus Limited',
    phoneNumber: '85221519107',
    emailAddress: 'info@shoptoplus.com',
  },
};

const DeliveryProviderModal: React.FC<DeliveryProviderModalProps> = ({
  visible,
  onClose,
  outboundOrderId,
  outboundNumber,
  recipientName,
  recipientAddress,
  recipientCountryCode,
  recipientPhone,
  recipientPostalCode,
  recipientCity,
  recipientState,
  totalAmount,
  onSuccess,
}) => {
    const { t } = useTranslation();

  // Country to default city and postal code info mapping. `city` is a form
  // default sent to the carrier API as the recipient address — data, not
  // copy, so it stays untranslated. `postalCodePlaceholder` / `statePlaceholder`
  // / state-option `label`s are UI hints and display text, so they are
  // translated. Kept inside the component (not module scope) so useTranslation
  // is in scope; memoised on `t` so it's stable within a render language.
  const countryDefaults = useMemo<Record<string, {
    city: string;
    postalCodeRequired: boolean;
    postalCodePlaceholder: string;
    stateRequired: boolean;
    statePlaceholder?: string;
    stateOptions?: { value: string; label: string }[];
  }>>(() => ({
    HK: { city: 'Hong Kong', postalCodeRequired: false, postalCodePlaceholder: t('可留空'), stateRequired: false },
    CN: { city: 'Shanghai', postalCodeRequired: true, postalCodePlaceholder: t('例如: 200000'), stateRequired: false },
    TW: { city: 'Taipei', postalCodeRequired: true, postalCodePlaceholder: t('例如: 10050'), stateRequired: false },
    US: {
      city: 'New York',
      postalCodeRequired: true,
      postalCodePlaceholder: t('例如: 10001'),
      stateRequired: true,
      statePlaceholder: t('例如: NY, CA'),
      stateOptions: [
        { value: 'AL', label: t('AL - Alabama') }, { value: 'AK', label: t('AK - Alaska') },
        { value: 'AZ', label: t('AZ - Arizona') }, { value: 'AR', label: t('AR - Arkansas') },
        { value: 'CA', label: t('CA - California') }, { value: 'CO', label: t('CO - Colorado') },
        { value: 'CT', label: t('CT - Connecticut') }, { value: 'DE', label: t('DE - Delaware') },
        { value: 'FL', label: t('FL - Florida') }, { value: 'GA', label: t('GA - Georgia') },
        { value: 'HI', label: t('HI - Hawaii') }, { value: 'ID', label: t('ID - Idaho') },
        { value: 'IL', label: t('IL - Illinois') }, { value: 'IN', label: t('IN - Indiana') },
        { value: 'IA', label: t('IA - Iowa') }, { value: 'KS', label: t('KS - Kansas') },
        { value: 'KY', label: t('KY - Kentucky') }, { value: 'LA', label: t('LA - Louisiana') },
        { value: 'ME', label: t('ME - Maine') }, { value: 'MD', label: t('MD - Maryland') },
        { value: 'MA', label: t('MA - Massachusetts') }, { value: 'MI', label: t('MI - Michigan') },
        { value: 'MN', label: t('MN - Minnesota') }, { value: 'MS', label: t('MS - Mississippi') },
        { value: 'MO', label: t('MO - Missouri') }, { value: 'MT', label: t('MT - Montana') },
        { value: 'NE', label: t('NE - Nebraska') }, { value: 'NV', label: t('NV - Nevada') },
        { value: 'NH', label: t('NH - New Hampshire') }, { value: 'NJ', label: t('NJ - New Jersey') },
        { value: 'NM', label: t('NM - New Mexico') }, { value: 'NY', label: t('NY - New York') },
        { value: 'NC', label: t('NC - North Carolina') }, { value: 'ND', label: t('ND - North Dakota') },
        { value: 'OH', label: t('OH - Ohio') }, { value: 'OK', label: t('OK - Oklahoma') },
        { value: 'OR', label: t('OR - Oregon') }, { value: 'PA', label: t('PA - Pennsylvania') },
        { value: 'RI', label: t('RI - Rhode Island') }, { value: 'SC', label: t('SC - South Carolina') },
        { value: 'SD', label: t('SD - South Dakota') }, { value: 'TN', label: t('TN - Tennessee') },
        { value: 'TX', label: t('TX - Texas') }, { value: 'UT', label: t('UT - Utah') },
        { value: 'VT', label: t('VT - Vermont') }, { value: 'VA', label: t('VA - Virginia') },
        { value: 'WA', label: t('WA - Washington') }, { value: 'WV', label: t('WV - West Virginia') },
        { value: 'WI', label: t('WI - Wisconsin') }, { value: 'WY', label: t('WY - Wyoming') },
      ],
    },
    GB: { city: 'London', postalCodeRequired: true, postalCodePlaceholder: t('例如: SW1A 1AA'), stateRequired: false },
    SG: { city: 'Singapore', postalCodeRequired: true, postalCodePlaceholder: t('例如: 018956 (6位數字)'), stateRequired: false },
    JP: { city: 'Tokyo', postalCodeRequired: true, postalCodePlaceholder: t('例如: 100-0001'), stateRequired: false },
    KR: { city: 'Seoul', postalCodeRequired: true, postalCodePlaceholder: t('例如: 04524'), stateRequired: false },
    AU: {
      city: 'Sydney',
      postalCodeRequired: true,
      postalCodePlaceholder: t('例如: 2000'),
      stateRequired: true,
      statePlaceholder: t('例如: NSW, VIC'),
      stateOptions: [
        { value: 'NSW', label: t('NSW - New South Wales') },
        { value: 'VIC', label: t('VIC - Victoria') },
        { value: 'QLD', label: t('QLD - Queensland') },
        { value: 'SA', label: t('SA - South Australia') },
        { value: 'WA', label: t('WA - Western Australia') },
        { value: 'TAS', label: t('TAS - Tasmania') },
        { value: 'NT', label: t('NT - Northern Territory') },
        { value: 'ACT', label: t('ACT - Australian Capital Territory') },
      ],
    },
    CA: {
      city: 'Toronto',
      postalCodeRequired: true,
      postalCodePlaceholder: t('例如: M5V 1J2'),
      stateRequired: true,
      statePlaceholder: t('例如: ON, BC'),
      stateOptions: [
        { value: 'AB', label: t('AB - Alberta') }, { value: 'BC', label: t('BC - British Columbia') },
        { value: 'MB', label: t('MB - Manitoba') }, { value: 'NB', label: t('NB - New Brunswick') },
        { value: 'NL', label: t('NL - Newfoundland and Labrador') }, { value: 'NS', label: t('NS - Nova Scotia') },
        { value: 'NT', label: t('NT - Northwest Territories') }, { value: 'NU', label: t('NU - Nunavut') },
        { value: 'ON', label: t('ON - Ontario') }, { value: 'PE', label: t('PE - Prince Edward Island') },
        { value: 'QC', label: t('QC - Quebec') }, { value: 'SK', label: t('SK - Saskatchewan') },
        { value: 'YT', label: t('YT - Yukon') },
      ],
    },
    MY: { city: 'Kuala Lumpur', postalCodeRequired: true, postalCodePlaceholder: t('例如: 50000'), stateRequired: false },
    TH: { city: 'Bangkok', postalCodeRequired: true, postalCodePlaceholder: t('例如: 10110'), stateRequired: false },
    PH: { city: 'Manila', postalCodeRequired: true, postalCodePlaceholder: t('例如: 1000'), stateRequired: false },
    ID: { city: 'Jakarta', postalCodeRequired: true, postalCodePlaceholder: t('例如: 10110'), stateRequired: false },
    VN: { city: 'Ho Chi Minh City', postalCodeRequired: true, postalCodePlaceholder: t('例如: 700000'), stateRequired: false },
    NZ: { city: 'Auckland', postalCodeRequired: true, postalCodePlaceholder: t('例如: 1010'), stateRequired: false },
  }), [t]);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState<string>('FedEx');
  const [result, setResult] = useState<{
    success: boolean;
    trackingNumber?: string;
    labelPdf?: string;
    error?: string;
  } | null>(null);
  const [fedexConfigured, setFedexConfigured] = useState<boolean | null>(null);
  const [sfExpressConfigured, setSfExpressConfigured] = useState<boolean | null>(null);
  const [sfExpressB2BConfigured, setSfExpressB2BConfigured] = useState<boolean | null>(null);
  const [carrierOptions, setCarrierOptions] = useState<Array<{
    carrierType: 'sf_express' | 'fedex';
    configId: string;
    accountType?: 'standard' | 'b2b';
    isProduction: boolean;
    displayLabel: string;
    defaultPayMethod?: 1 | 3;
  }>>([]);

  // Map the new "carrierType:configId" provider value back to the legacy
  // 'FedEx' | 'SF' | 'SF_B2B' string so all existing conditional rendering keeps
  // working without rewriting every condition in this large file.
  const selectedOption = carrierOptions.find(
    (o) => `${o.carrierType}:${o.configId}` === provider,
  );
  const legacyProvider: 'FedEx' | 'SF' | 'SF_B2B' | string = selectedOption
    ? selectedOption.carrierType === 'fedex'
      ? 'FedEx'
      : selectedOption.accountType === 'b2b'
        ? 'SF_B2B'
        : 'SF'
    : provider;

  // When the user picks a different carrier in the dropdown, reset the SF
  // payment-method form field to that carrier's default (3 = monthly if cust_id
  // is set on the config, 1 = prepaid if not). The operator can still override
  // via the 付款方式 dropdown.
  useEffect(() => {
    if (selectedOption?.carrierType === 'sf_express' && selectedOption.defaultPayMethod) {
      form.setFieldsValue({ sfPayMethod: selectedOption.defaultPayMethod });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption?.configId, selectedOption?.defaultPayMethod]);

  // Default to the passed country code, or 'HK' if not provided
  const [selectedRecipientCountry, setSelectedRecipientCountry] = useState<string>(recipientCountryCode || 'HK');

  // Normalize an order's state/province to the country's option codes so the
  // dropdown pre-selects correctly. Falls back to '' (operator must choose) when
  // the value doesn't map to a known code — safer than pre-filling a wrong state.
  const normalizeState = (rawState: string | undefined, countryCode: string): string => {
    const raw = (rawState && rawState !== '-') ? String(rawState).trim().toUpperCase() : '';
    if (!raw) return '';
    const opts = (countryDefaults[countryCode] || countryDefaults['HK']).stateOptions;
    if (!opts) return raw; // country uses a free-text state input
    const match = opts.find(
      (o) =>
        o.value.toUpperCase() === raw ||
        (o.label.split(' - ')[1] || '').trim().toUpperCase() === raw,
    );
    return match ? match.value : '';
  };

  // The order's own shipping address (from Shopify / the outbound order), with
  // "-" placeholders removed. This is the source of truth the form is pre-filled
  // from, and what a submitted label is checked against.
  const orderAddress = useMemo(() => {
    const clean = (v?: string) => (v && v !== '-') ? String(v).trim() : '';
    const countryCode = recipientCountryCode || 'HK';
    return {
      countryCode,
      name: clean(recipientName),
      address: clean(recipientAddress),
      phone: clean(recipientPhone),
      city: clean(recipientCity),
      postalCode: clean(recipientPostalCode),
      state: normalizeState(recipientState, countryCode),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientCountryCode, recipientName, recipientAddress, recipientPhone, recipientCity, recipientPostalCode, recipientState, countryDefaults]);

  // City to pre-fill when the order has none. Only for city-states, where the
  // city IS the country; anywhere else a guessed capital ships the parcel to the
  // wrong city (14–19 Oct 2026: 23 labels went out as Sydney/Auckland/Manila/
  // Kuala Lumpur), so the field is left empty and required instead.
  const CITY_STATES = ['HK', 'SG', 'MO'];
  const fallbackCity = (countryCode: string) =>
    CITY_STATES.includes(countryCode) ? (countryDefaults[countryCode]?.city || '') : '';

  // Handle recipient country change. Never overwrite the order's real address:
  // switching back to the order's own country restores its city/postcode/state;
  // switching to a different country clears them so the operator must enter the
  // new address, rather than silently pre-filling a capital city.
  const handleRecipientCountryChange = (countryCode: string) => {
    setSelectedRecipientCountry(countryCode);
    const isOrderCountry = countryCode === orderAddress.countryCode;
    setTimeout(() => {
      form.setFieldsValue({
        recipientCity: isOrderCountry ? (orderAddress.city || fallbackCity(countryCode)) : fallbackCity(countryCode),
        recipientPostalCode: isOrderCountry ? orderAddress.postalCode : '',
        recipientState: isOrderCountry ? orderAddress.state : '',
      });
    }, 0);
  };

  // Check delivery provider configurations on mount and initialize form with order data
  useEffect(() => {
    if (visible) {
      checkFedExConfig();
      checkSFExpressConfig();
      checkSFExpressB2BConfig();
      loadCarrierOptions();
      setResult(null);

      // Initialize form with order data (from Shopify). Every field is set on
      // each open (even to ''), which also clears stale values from a prior order.
      const { countryCode, name, address, phone, city, postalCode, state } = orderAddress;
      setSelectedRecipientCountry(countryCode);

      setTimeout(() => {
        form.setFieldsValue({
          // FedEx recipient fields
          recipientName: name,
          recipientAddress: address,
          recipientCountry: countryCode,
          recipientCity: city || fallbackCity(countryCode),
          recipientPostalCode: postalCode,
          recipientState: state,
          recipientPhone: phone,
          // SF Express recipient fields
          sfRecipientName: name,
          sfRecipientAddress: address,
          sfRecipientPhone: phone,
          sfRecipientCity: city,
          // Auto-populate declared value from order total (for cross-border shipments)
          sfDeclaredValue: totalAmount && totalAmount > 0 ? totalAmount : undefined,
          sfDeclaredValueCurrency: 'HKD',
        });
      }, 0);
    }
  }, [visible, recipientName, recipientAddress, recipientCountryCode, recipientPhone, recipientPostalCode, recipientCity, recipientState]);

  const checkFedExConfig = async () => {
    try {
      const response = await apiService.getFedExConfig();
      setFedexConfigured(response.success && response.data?.clientId);
    } catch (error) {
      setFedexConfigured(false);
    }
  };

  const checkSFExpressConfig = async () => {
    try {
      const response = await apiService.getSFExpressConfig();
      setSfExpressConfigured(response.success && response.data?.clientCode);
    } catch (error) {
      setSfExpressConfigured(false);
    }
  };

  const checkSFExpressB2BConfig = async () => {
    try {
      const response = await apiService.getSFExpressConfig('b2b');
      setSfExpressB2BConfigured(response.success && response.data?.clientCode);
    } catch (error) {
      setSfExpressB2BConfigured(false);
    }
  };

  const loadCarrierOptions = async () => {
    try {
      const res: any = await apiService.getCarrierOptionsForOrder(outboundOrderId);
      if (res?.success && Array.isArray(res.data)) {
        setCarrierOptions(res.data);
      } else {
        setCarrierOptions([]);
      }
    } catch (e) {
      console.error('Failed to load carrier options:', e);
      setCarrierOptions([]);
    }
  };

  const handleCreateShipment = async () => {
    // Multi-config flow: provider is "carrierType:configId"
    if (selectedOption) {
      if (selectedOption.carrierType === 'sf_express') {
        await handleCreateSFExpressShipment(
          selectedOption.accountType ?? 'standard',
          selectedOption.configId,
        );
      } else if (selectedOption.carrierType === 'fedex') {
        await handleCreateFedExShipment(selectedOption.configId);
      }
      return;
    }

    // Legacy fallback (in case server returns no options for some reason)
    if (legacyProvider === 'FedEx') {
      await handleCreateFedExShipment();
    } else if (legacyProvider === 'SF') {
      await handleCreateSFExpressShipment('standard');
    } else if (legacyProvider === 'SF_B2B') {
      await handleCreateSFExpressShipment('b2b');
    } else {
      message.info(t('{{provider}} 整合即將推出', { provider }));
    }
  };

  const handleCreateSFExpressShipment = async (
    accountType: 'standard' | 'b2b' = 'standard',
    configId?: string,
  ) => {
    try {
      setLoading(true);
      setResult(null);

      // Get shipper and recipient info from form
      const values = form.getFieldsValue();
      const shipperInfo = {
        name: values.sfShipperName || values.shipperCompany,
        address: values.sfShipperAddress || values.shipperAddress,
        phone: values.sfShipperPhone || values.shipperPhone,
        province: values.sfShipperProvince,
        city: values.sfShipperCity,
        cargoName: values.sfCargoName || '貨品',
      };

      // Recipient info for SF Express
      const recipientInfo = {
        name: values.sfRecipientName || '',
        address: values.sfRecipientAddress || '',
        phone: values.sfRecipientPhone || '',
        province: values.sfRecipientProvince || '',
        city: values.sfRecipientCity || '',
      };

      const response: any = await apiService.createSFExpressOutboundShipment(
        outboundOrderId,
        shipperInfo,
        {
          expressTypeId: values.sfExpressType,
          payMethod: values.sfPayMethod,
          recipientInfo,
          accountType,
          configId, // multi-config flow: backend prefers this when present
          declaredValue: values.sfDeclaredValue ? String(values.sfDeclaredValue) : undefined,
          declaredValueCurrency: values.sfDeclaredValue ? (values.sfDeclaredValueCurrency || 'HKD') : undefined,
        }
      );

      if (response.success) {
        // Try to get the waybill PDF
        let labelPdf: string | undefined;
        if (response.data?.hasWaybillPdf) {
          try {
            const labelResponse: any = await apiService.getSFExpressOutboundLabel(outboundOrderId, 'base64');
            if (labelResponse.success && labelResponse.data?.waybillPdf) {
              labelPdf = labelResponse.data.waybillPdf;
            }
          } catch (e) {
            console.warn('Failed to get waybill PDF:', e);
          }
        }

        setResult({
          success: true,
          trackingNumber: response.data?.trackingNumber,
          labelPdf,
        });
        message.success(t('運單已建立！追蹤號碼：{{trackingNumber}}', { trackingNumber: response.data?.trackingNumber }));

        if (onSuccess) {
          onSuccess(response.data?.trackingNumber, labelPdf || '');
        }
      } else {
        setResult({
          success: false,
          error: response.message || response.error || t('建立運單失敗'),
        });
        message.error(response.message || response.error || t('建立運單失敗'));
      }
    } catch (error: any) {
      console.error('Create SF Express shipment error:', error);
      setResult({
        success: false,
        error: error.message || t('建立運單時發生錯誤'),
      });
      message.error(error.message || t('建立運單時發生錯誤'));
    } finally {
      setLoading(false);
    }
  };

  // Before a FedEx label is created, compare the recipient fields with the
  // order's own address. A mismatch is not always wrong (the operator may be
  // correcting the order), so ask instead of blocking — but never let a changed
  // city/postcode go out silently. Only fields the order actually has are checked.
  const confirmAddressMatchesOrder = (values: any): Promise<'match' | 'confirmed' | 'cancelled'> => {
    const norm = (v: unknown) => String(v ?? '').toUpperCase().replace(/[\s\-.,'’]/g, '');
    const blank = t('(空白)');
    const checks = [
      { label: t('收件人'), order: orderAddress.name, form: values.recipientName },
      { label: t('收件國家代碼'), order: recipientCountryCode || '', form: values.recipientCountry },
      { label: t('收件城市'), order: orderAddress.city, form: values.recipientCity },
      { label: t('收件郵遞區號'), order: orderAddress.postalCode, form: values.recipientPostalCode },
    ];
    const diffs = checks.filter((c) => c.order && norm(c.order) !== norm(c.form));
    if (!diffs.length) return Promise.resolve('match');

    return new Promise((resolve) => {
      Modal.confirm({
        title: t('收件地址與訂單不一致'),
        width: 560,
        content: (
          <div>
            <p>{t('以下欄位與訂單資料不同，確定要用表單上的資料建立運單嗎？')}</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}></th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('訂單資料')}</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('運單資料')}</th>
                </tr>
              </thead>
              <tbody>
                {diffs.map((d) => (
                  <tr key={d.label}>
                    <td style={{ padding: '4px 8px' }}>{d.label}</td>
                    <td style={{ padding: '4px 8px' }}>{d.order || blank}</td>
                    <td style={{ padding: '4px 8px', color: '#cf1322', fontWeight: 600 }}>{d.form || blank}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
        okText: t('仍然建立'),
        cancelText: t('返回修改'),
        onOk: () => resolve('confirmed'),
        onCancel: () => resolve('cancelled'),
      });
    });
  };

  const handleCreateFedExShipment = async (configId?: string) => {
    // The submit button calls this directly (not via form submit), so the
    // recipient fields' `required` rules must be enforced here — otherwise a
    // blank city/postcode reaches FedEx.
    try {
      await form.validateFields([
        'recipientName', 'recipientAddress', 'recipientCountry', 'recipientCity',
        'recipientPostalCode', 'recipientState', 'recipientPhone',
      ]);
    } catch {
      message.error(t('請填寫所有必填的收件資料'));
      return;
    }
    const addressCheck = await confirmAddressMatchesOrder(form.getFieldsValue());
    if (addressCheck === 'cancelled') {
      return;
    }

    try {
      setLoading(true);
      setResult(null);

      // Get shipper info from form
      const values = form.getFieldsValue();
      const shipperInfo = {
        address: {
          streetLines: [values.shipperAddress],
          city: values.shipperCity,
          postalCode: values.shipperPostalCode,
          countryCode: values.shipperCountry,
        },
        contact: {
          companyName: values.shipperCompany,
          phoneNumber: values.shipperPhone,
          emailAddress: values.shipperEmail,
        },
      };

      // Build recipient info from form
      console.log('[DeliveryProviderModal] Form values before submission:', {
        recipientName: values.recipientName,
        recipientAddress: values.recipientAddress,
        recipientCountry: values.recipientCountry,
        recipientCity: values.recipientCity,
        recipientPostalCode: values.recipientPostalCode,
        recipientState: values.recipientState,
        recipientPhone: values.recipientPhone,
      });
      const recipientInfo = {
        personName: values.recipientName || '',
        streetLines: values.recipientAddress || '',
        countryCode: values.recipientCountry || 'HK',
        postalCode: values.recipientPostalCode || '',
        city: values.recipientCity || '',
        stateOrProvinceCode: values.recipientState || '',
        phone: values.recipientPhone || '',
      };
      console.log('[DeliveryProviderModal] Sending recipientInfo to backend:', recipientInfo);

      const response = await apiService.createFedExOutboundShipment(
        outboundOrderId,
        shipperInfo,
        {
          serviceType: values.serviceType,
          packagingType: values.packagingType,
          recipientInfo,
          configId, // multi-config flow: backend prefers this when present
          addressMismatchConfirmed: addressCheck === 'confirmed',
        }
      );

      if (response.success) {
        setResult({
          success: true,
          trackingNumber: response.data?.trackingNumber,
          labelPdf: response.data?.labelPdf,
        });
        message.success(t('運單已建立！追蹤號碼：{{trackingNumber}}', { trackingNumber: response.data?.trackingNumber }));

        if (onSuccess) {
          onSuccess(response.data?.trackingNumber, response.data?.labelPdf);
        }
      } else {
        setResult({
          success: false,
          error: response.message || t('建立運單失敗'),
        });
        message.error(response.message || t('建立運單失敗'));
      }
    } catch (error: any) {
      console.error('Create shipment error:', error);
      setResult({
        success: false,
        error: error.message || t('建立運單時發生錯誤'),
      });
      message.error(error.message || t('建立運單時發生錯誤'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabel = async () => {
    if (!result?.labelPdf) return;

    try {
      let blob: Blob;

      if (legacyProvider === 'FedEx') {
        // Use backend API to get resized 4x6 label
        console.log('[FedEx Label] Fetching resized 4x6 label from backend...');
        blob = await apiService.getFedExOutboundLabel(outboundOrderId, 'pdf', '4x6') as Blob;
      } else {
        // For other providers, use original PDF from result
        const byteCharacters = atob(result.labelPdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const pdfBytes = new Uint8Array(byteNumbers);
        blob = new Blob([pdfBytes], { type: 'application/pdf' });
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${legacyProvider === 'SF' || legacyProvider === 'SF_B2B' ? 'sf-express' : 'fedex'}-label-${outboundNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(t('運單標籤已下載'));
    } catch (error) {
      console.error('Download label error:', error);
      message.error(t('下載運單標籤失敗'));
    }
  };

  const handlePrintLabel = async () => {
    if (!result?.labelPdf) return;

    try {
      let blob: Blob;

      if (legacyProvider === 'FedEx') {
        // Use backend API to get resized 4x6 label
        console.log('[FedEx Label] Fetching resized 4x6 label from backend for printing...');
        blob = await apiService.getFedExOutboundLabel(outboundOrderId, 'pdf', '4x6') as Blob;
      } else {
        // For other providers, use original PDF from result
        const byteCharacters = atob(result.labelPdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const pdfBytes = new Uint8Array(byteNumbers);
        blob = new Blob([pdfBytes], { type: 'application/pdf' });
      }

      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      console.error('Print label error:', error);
      message.error(t('列印運單標籤失敗'));
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SendOutlined />
          {t('選擇物流服務商')}
                          </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={null}
      destroyOnClose
    >
      {/* Order Info */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label={t('出庫單號')}>{outboundNumber}</Descriptions.Item>
          <Descriptions.Item label={t('收件人')}>{recipientName || ''}</Descriptions.Item>
          <Descriptions.Item label={t('收件地址')} span={2}>
            {recipientAddress || ''}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Result Display */}
      {result && (
        <Alert
          message={result.success ? t('運單建立成功') : t('運單建立失敗')}
          description={
            result.success ? (
              <div>
                <p>
                  <strong>{t('追蹤號碼：')}</strong> {result.trackingNumber}
                </p>
                <Space style={{ marginTop: 8 }}>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadLabel}
                    disabled={!result.labelPdf}
                  >
                    {t('下載運單')}
                                                </Button>
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={handlePrintLabel}
                    disabled={!result.labelPdf}
                  >
                    {t('列印運單')}
                                                </Button>
                </Space>
              </div>
            ) : (
              result.error
            )
          }
          type={result.success ? 'success' : 'error'}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            provider: 'FedEx',
            serviceType: 'FEDEX_INTERNATIONAL_CONNECT_PLUS',
            packagingType: 'YOUR_PACKAGING',
            shipperCompany: defaultShipperInfo.contact.companyName,
            shipperAddress: defaultShipperInfo.address.streetLines[0],
            shipperCity: defaultShipperInfo.address.city,
            shipperCountry: defaultShipperInfo.address.countryCode,
            shipperPostalCode: defaultShipperInfo.address.postalCode,
            shipperPhone: defaultShipperInfo.contact.phoneNumber,
            shipperEmail: defaultShipperInfo.contact.emailAddress,
          }}
        >
          {/* Delivery Provider Selection — server-filtered per outbound order's customer */}
          <Form.Item
            name="provider"
            label={t('物流服務商')}
            rules={[{ required: true, message: t('請選擇物流服務商') }]}
          >
            <Select
              size="large"
              onChange={(value) => setProvider(value)}
              style={{ width: '100%' }}
              placeholder={carrierOptions.length === 0 ? t('無可用的物流服務商') : t('選擇物流服務商')}
            >
              {carrierOptions.map((opt) => (
                <Option
                  key={`${opt.carrierType}:${opt.configId}`}
                  value={`${opt.carrierType}:${opt.configId}`}
                >
                  <Space>
                    {opt.carrierType === 'fedex' && (
                      <img
                        src="https://www.fedex.com/content/dam/fedex-com/logos/logo.png"
                        alt="FedEx"
                        style={{ height: 20 }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    {opt.displayLabel}
                  </Space>
                </Option>
              ))}
              <Option value="Gobuyship" disabled>
                <Space>
                  {t('Gobuyship (即將推出)')}
                                                  </Space>
              </Option>
            </Select>
          </Form.Item>

          {/* FedEx Configuration Warning */}
          {!selectedOption && legacyProvider === 'FedEx' && fedexConfigured === false && (
            <Alert
              message={t('FedEx 尚未配置')}
              description={t('請先在 設置 > FedEx 配置 頁面配置 FedEx API 憑證')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* FedEx Options */}
          {legacyProvider === 'FedEx' && (
            <>
              <Divider orientation="left">{t('FedEx 服務選項')}</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="serviceType"
                    label={t('服務類型')}
                    rules={[{ required: true }]}
                  >
                    <Select>
                      <Option value="FEDEX_INTERNATIONAL_CONNECT_PLUS">
                        {t('FedEx International Connect Plus (FICP)')}
                                                                    </Option>
                      <Option value="FEDEX_INTERNATIONAL_PRIORITY">
                        {t('FedEx International Priority (IP)')}
                                                                    </Option>
                      <Option value="FEDEX_INTERNATIONAL_ECONOMY">
                        {t('FedEx International Economy')}
                                                                    </Option>
                      <Option value="INTERNATIONAL_FIRST">
                        {t('FedEx International First')}
                                                                    </Option>
                      <Option value="FEDEX_GROUND">
                        {t('FedEx Ground')}
                                                                    </Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="packagingType"
                    label={t('包裝類型')}
                    rules={[{ required: true }]}
                  >
                    <Select>
                      <Option value="YOUR_PACKAGING">{t('自有包裝')}</Option>
                      <Option value="FEDEX_BOX">{t('FedEx Box')}</Option>
                      <Option value="FEDEX_ENVELOPE">{t('FedEx Envelope')}</Option>
                      <Option value="FEDEX_PAK">{t('FedEx Pak')}</Option>
                      <Option value="FEDEX_TUBE">{t('FedEx Tube')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">{t('寄件人資訊')}</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="shipperCompany"
                    label={t('公司名稱')}
                    rules={[{ required: true, message: t('請輸入公司名稱') }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="shipperPhone"
                    label={t('電話號碼')}
                    rules={[{ required: true, message: t('請輸入電話號碼') }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="shipperAddress"
                label={t('地址')}
                rules={[{ required: true, message: t('請輸入地址') }]}
              >
                <Input />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="shipperCity"
                    label={t('城市')}
                    rules={[{ required: true, message: t('請輸入城市') }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="shipperCountry"
                    label={t('國家代碼')}
                    rules={[{ required: true, message: t('請輸入國家代碼') }]}
                  >
                    <Select>
                      <Option value="HK">{t('HK - 香港')}</Option>
                      <Option value="CN">{t('CN - 中國')}</Option>
                      <Option value="TW">{t('TW - 台灣')}</Option>
                      <Option value="US">{t('US - 美國')}</Option>
                      <Option value="GB">{t('GB - 英國')}</Option>
                      <Option value="SG">{t('SG - 新加坡')}</Option>
                      <Option value="JP">{t('JP - 日本')}</Option>
                      <Option value="KR">{t('KR - 韓國')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="shipperPostalCode"
                    label={t('郵遞區號')}
                    rules={[{ required: true, message: t('請輸入郵遞區號') }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="shipperEmail" label={t('電子郵件')}>
                <Input type="email" />
              </Form.Item>

              <Divider orientation="left">{t('收件人資訊 (FedEx 需要)')}</Divider>

              {/* Warning when HK is selected - FedEx doesn't do domestic HK */}
              {selectedRecipientCountry === 'HK' && (
                <Alert
                  message={t('FedEx 僅提供國際快遞服務')}
                  description={t('FedEx 不提供香港本地配送服務。如需香港本地配送，請使用順豐速運。如果這是國際訂單，請選擇正確的收件國家。')}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="recipientName"
                    label={t('收件人姓名')}
                    rules={[{ required: true, message: t('請輸入收件人姓名') }]}
                    tooltip={t('FedEx 需要收件人姓名')}
                  >
                    <Input placeholder={t('收件人姓名')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="recipientPhone"
                    label={t('收件人電話')}
                    rules={[{ required: true, message: t('請輸入收件人電話') }]}
                    tooltip={t('FedEx 需要收件人電話號碼')}
                  >
                    <Input placeholder={t('例如: 85212345678')} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="recipientAddress"
                label={t('收件地址')}
                rules={[{ required: true, message: t('請輸入收件地址') }]}
                tooltip={t('FedEx 需要完整的收件地址')}
              >
                <Input placeholder={t('詳細收件地址')} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="recipientCountry"
                    label={t('收件國家代碼')}
                    rules={[{ required: true, message: t('請選擇收件國家') }]}
                    initialValue="HK"
                    tooltip={t('FedEx 僅提供國際快遞服務。請選擇正確的收件國家。')}
                  >
                    <Select onChange={handleRecipientCountryChange} showSearch optionFilterProp="children">
                      <Option value="HK">{t('HK - 香港')}</Option>
                      <Option value="CN">{t('CN - 中國')}</Option>
                      <Option value="TW">{t('TW - 台灣')}</Option>
                      <Option value="US">{t('US - 美國')}</Option>
                      <Option value="CA">{t('CA - 加拿大')}</Option>
                      <Option value="GB">{t('GB - 英國')}</Option>
                      <Option value="SG">{t('SG - 新加坡')}</Option>
                      <Option value="JP">{t('JP - 日本')}</Option>
                      <Option value="KR">{t('KR - 韓國')}</Option>
                      <Option value="AU">{t('AU - 澳洲')}</Option>
                      <Option value="MY">{t('MY - 馬來西亞')}</Option>
                      <Option value="TH">{t('TH - 泰國')}</Option>
                      <Option value="PH">{t('PH - 菲律賓')}</Option>
                      <Option value="ID">{t('ID - 印尼')}</Option>
                      <Option value="VN">{t('VN - 越南')}</Option>
                      <Option value="NZ">{t('NZ - 紐西蘭')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="recipientCity"
                    label={t('收件城市')}
                    rules={[{ required: true, message: t('請輸入收件城市') }]}
                    initialValue="Hong Kong"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="recipientPostalCode"
                    label={t('收件郵遞區號')}
                    tooltip={countryDefaults[selectedRecipientCountry]?.postalCodeRequired
                      ? t('此國家需要郵遞區號')
                      : t('此國家不需要郵遞區號')}
                    rules={countryDefaults[selectedRecipientCountry]?.postalCodeRequired
                      ? [{ required: true, message: t('此國家需要郵遞區號') }]
                      : []}
                  >
                    <Input
                      placeholder={countryDefaults[selectedRecipientCountry]?.postalCodePlaceholder || t('輸入郵遞區號')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* State/Province Code - Only show for countries that require it */}
              {countryDefaults[selectedRecipientCountry]?.stateRequired && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="recipientState"
                      label={t('收件州/省代碼')}
                      rules={[{ required: true, message: t('此國家需要州/省代碼') }]}
                      tooltip={`${
                        selectedRecipientCountry === 'US'
                          ? t('美國需要州代碼')
                          : selectedRecipientCountry === 'AU'
                          ? t('澳洲需要州代碼')
                          : selectedRecipientCountry === 'CA'
                          ? t('加拿大需要省代碼')
                          : t('此國家需要州/省代碼')
                      }${t('，請從下拉選單選擇')}`}
                    >
                      {countryDefaults[selectedRecipientCountry]?.stateOptions ? (
                        <Select
                          placeholder={countryDefaults[selectedRecipientCountry]?.statePlaceholder || t('選擇州/省')}
                          showSearch
                          optionFilterProp="children"
                        >
                          {countryDefaults[selectedRecipientCountry]?.stateOptions?.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      ) : (
                        <Input placeholder={countryDefaults[selectedRecipientCountry]?.statePlaceholder || t('輸入州/省代碼')} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </>
          )}

          {/* SF Express Configuration Warning */}
          {!selectedOption && legacyProvider === 'SF' && sfExpressConfigured === false && (
            <Alert
              message={t('順豐速運 (標準) 尚未配置')}
              description={t('請先在 設置 > 順豐速運配置 頁面配置 SF Express API 憑證')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* SF Express B2B Configuration Warning */}
          {!selectedOption && legacyProvider === 'SF_B2B' && sfExpressB2BConfigured === false && (
            <Alert
              message={t('順豐速運 B2B 尚未配置')}
              description={t('請先在 設置 > 順豐速運 B2B 配置 頁面配置 SF Express B2B API 憑證')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* SF Express Options (for both standard and B2B) */}
          {(legacyProvider === 'SF' || legacyProvider === 'SF_B2B') && (
            <>
              <Divider orientation="left">{t('順豐服務選項')}</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="sfExpressType"
                    label={t('服務類型')}
                    initialValue={1}
                    rules={[{ required: true }]}
                  >
                    <Select>
                      <Option value={1}>{t('順豐次日 (T4)')}</Option>
                      <Option value={2}>{t('順豐標快 (T6) - 2-3天')}</Option>
                      <Option value={6}>{t('順豐即日 (T104)')}</Option>
                      <Option value={263}>{t('同城半日達 (T11)')}</Option>
                      <Option value={10}>{t('國際小包 (T14)')}</Option>
                      <Option value={12}>{t('國際特惠 (T7)')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sfPayMethod"
                    label={t('付款方式')}
                    initialValue={3}
                    rules={[{ required: true }]}
                  >
                    <Select>
                      <Option value={3}>{t('寄付月結')}</Option>
                      <Option value={1}>{t('寄付現結')}</Option>
                      <Option value={2}>{t('到付')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="sfCargoName"
                label={t('貨品名稱')}
                initialValue="貨品"
              >
                <Input placeholder={t('例如：服裝、電子產品等')} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="sfDeclaredValue"
                    label={t('申報價值')}
                    tooltip={t('跨境件必須填寫申報價值（如寄往澳門、中國大陸等）')}
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder={t('貨物申報價值')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sfDeclaredValueCurrency"
                    label={t('幣別')}
                    initialValue="HKD"
                  >
                    <Select>
                      <Option value="HKD">{t('HKD - 港幣')}</Option>
                      <Option value="CNY">{t('CNY - 人民幣')}</Option>
                      <Option value="USD">{t('USD - 美元')}</Option>
                      <Option value="MOP">{t('MOP - 澳門幣')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">{t('寄件人資訊')}</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="sfShipperName"
                    label={t('寄件人名稱')}
                    initialValue={defaultShipperInfo.contact.companyName}
                    rules={[{ required: true, message: t('請輸入寄件人名稱') }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sfShipperPhone"
                    label={t('電話號碼')}
                    initialValue={defaultShipperInfo.contact.phoneNumber}
                    rules={[{ required: true, message: t('請輸入電話號碼') }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="sfShipperAddress"
                label={t('地址')}
                initialValue={defaultShipperInfo.address.streetLines[0]}
                rules={[{ required: true, message: t('請輸入地址') }]}
              >
                <Input />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="sfShipperProvince"
                    label={t('省份')}
                    initialValue="香港"
                  >
                    <Input placeholder={t('例如：廣東省、香港')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sfShipperCity"
                    label={t('城市')}
                    initialValue={defaultShipperInfo.address.city}
                  >
                    <Input placeholder={t('例如：深圳市、香港')} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left">{t('收件人資訊')}</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="sfRecipientName"
                    label={t('收件人姓名')}
                    rules={[{ required: true, message: t('請輸入收件人姓名') }]}
                  >
                    <Input placeholder={t('收件人姓名')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sfRecipientPhone"
                    label={t('收件人電話')}
                    rules={[{ required: true, message: t('請輸入收件人電話') }]}
                  >
                    <Input placeholder={t('例如: 13800138000')} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="sfRecipientAddress"
                label={t('收件地址')}
                rules={[{ required: true, message: t('請輸入收件地址') }]}
              >
                <Input placeholder={t('詳細收件地址')} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="sfRecipientProvince"
                    label={t('省份')}
                  >
                    <Input placeholder={t('例如：廣東省、香港')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sfRecipientCity"
                    label={t('城市')}
                  >
                    <Input placeholder={t('例如：深圳市、香港')} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* Action Buttons */}
          <Divider />
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>{t('取消')}</Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleCreateShipment}
              loading={loading}
              disabled={
                // Multi-config flow: a selected option means we have a valid configId
                // to ship with, so don't disable based on legacy "configured" flags.
                !selectedOption && (
                  (legacyProvider === 'FedEx' && fedexConfigured === false) ||
                  (legacyProvider === 'SF' && sfExpressConfigured === false) ||
                  (legacyProvider === 'SF_B2B' && sfExpressB2BConfigured === false)
                )
              }
            >
              {t('建立運單')}
                                      </Button>
          </Space>
        </Form>
      </Spin>
    </Modal>
  );
};

export default DeliveryProviderModal;